import { HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter, Subscription, finalize } from "rxjs";
import { AuthService } from "./auth.service";
import { ApiService } from "./api.service";
import { getOrCreateAuditSessionCorrelationId, rotateAuditSessionCorrelationId } from "./audit-session.storage";
import { RespondentAuditService } from "./respondent-audit.service";
import { mirrorAdminAuditToRespondent, normalizeEnrollmentId } from "./respondent-audit.helper";
import { centralDateTimeToPlainBackend } from "../utils/datetime-local.util";

export type AuditEventType =
  | "AUTH_LOGIN_SUCCESS"
  | "AUTH_LOGOUT"
  | "AUTH_TOKEN_EXPIRED"
  | "AUTH_SESSION_END"
  | "NAVIGATE"
  | string;

export interface CreateAuditEventRequest {
  eventType: string;
  route?: string | null;
  label?: string | null;
  correlationId?: string | null;
  createdAtUtc?: string | null;
  metadataJson?: string | null;
}

@Injectable({
  providedIn: "root",
})
export class AuditService implements OnDestroy {
  private readonly endpoint = "auditevents/batch";

  private correlationId = getOrCreateAuditSessionCorrelationId();
  private rotateCorrelationAfterNextFlush = false;
  private queue: CreateAuditEventRequest[] = [];
  private flushTimer: any = null;
  private readonly maxBatchSize = 25;
  private initialized = false;
  private authTerminalSentAtUtcMs: number | null = null;
  private readonly sessionEndSentKey = "auditSessionEndSentForCorrelationId";
  private lastAdminActionFailureAtUtcMs: number | null = null;

  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private router: Router,
    private respondentAudit: RespondentAuditService,
  ) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Navigation events
    this.subs.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe((e) => {
          const url = (e as NavigationEnd).urlAfterRedirects || this.router.url;
          this.track("NAVIGATE", {
            route: url,
            label: "route_change",
            metadata: { url },
          });
        })
    );

    // Best-effort session end marker
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", (ev: PageTransitionEvent) => {
        if ((ev as any).persisted) return;
        this.sendSessionEndBeacon();
      });
    }

    // If auth is cleared, rotate correlation so next login starts a fresh session.
    let hadAuth = this.hasValidAuthForClientEvents();
    this.subs.add(
      this.auth.currentAuthState$.subscribe(() => {
        const has = this.hasValidAuthForClientEvents();
        if (hadAuth && !has) {
          this.rotateCorrelationAfterNextFlush = false;
          this.queue = [];
          this.rotateCorrelationId();
        }
        hadAuth = has;
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  trackAction(eventType: string, label: string, metadata: any, flushNow: boolean = false): void {
    // When a feature already logs a human-friendly admin failure event,
    // suppress the generic API_ERROR/http_error row that would otherwise duplicate it.
    if (
      String(eventType || "").toUpperCase() === "ACTION_ADMIN" &&
      /^admin_.*_failed$/i.test(String(label || ""))
    ) {
      this.lastAdminActionFailureAtUtcMs = Date.now();
    }
    this.track(
      eventType as AuditEventType,
      {
        route: this.router.url,
        label,
        metadata,
      },
      { flushNow }
    );

    if (
      String(eventType || "").toUpperCase() === "ACTION_ADMIN" &&
      label &&
      !/_submit$/i.test(label) &&
      !/_validate_submit$/i.test(label)
    ) {
      mirrorAdminAuditToRespondent(this.respondentAudit, this.auth, label, "ACTION_ADMIN", {
        metadata: metadata ?? null,
        subjectUserId: metadata?.context?.affectedUserId ?? metadata?.input?.userId ?? null,
        referenceId: metadata?.input?.referenceId ?? null,
        enrollmentId:
          normalizeEnrollmentId(metadata?.input?.enrollmentId)
          ?? normalizeEnrollmentId(metadata?.context?.entityId),
      });
    }
  }

  /** Returns true if we should suppress a generic http_error row (dedupe with admin_*_failed). */
  shouldSuppressHttpErrorAudit(nowUtcMs: number = Date.now()): boolean {
    if (!this.lastAdminActionFailureAtUtcMs) return false;
    return nowUtcMs - this.lastAdminActionFailureAtUtcMs <= 1500;
  }

  trackLogout(): void {
    this.rotateCorrelationAfterNextFlush = true;
    this.authTerminalSentAtUtcMs = Date.now();
    this.track("AUTH_LOGOUT", { route: this.router.url, label: "logout" }, { flushNow: true });
  }

  trackTokenExpired(): void {
    this.rotateCorrelationAfterNextFlush = true;
    this.authTerminalSentAtUtcMs = Date.now();
    this.track("AUTH_TOKEN_EXPIRED", { route: this.router.url, label: "token_expired" }, { flushNow: true });
  }

  private track(
    type: AuditEventType,
    data: { route?: string; label?: string; metadata?: any },
    options?: { flushNow?: boolean }
  ): void {
    if (!this.hasValidAuthForClientEvents()) return;

    const event: CreateAuditEventRequest = {
      eventType: type,
      route: data.route ?? this.router.url,
      label: data.label ?? null,
      correlationId: this.correlationId,
      createdAtUtc: centralDateTimeToPlainBackend(),
      metadataJson: data.metadata ? JSON.stringify(data.metadata) : null,
    };

    this.queue.push(event);

    if (options?.flushNow) {
      this.flushSoon(0);
      return;
    }

    this.flushSoon(this.queue.length >= this.maxBatchSize ? 0 : 1500);
  }

  private flushSoon(delayMs: number): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  private flush(): void {
    if (this.queue.length === 0) return;

    const token = this.auth.getToken();
    if (!token) {
      if (this.rotateCorrelationAfterNextFlush) {
        this.rotateCorrelationAfterNextFlush = false;
        this.queue = [];
        this.rotateCorrelationId();
      }
      return;
    }

    const batch = this.queue.splice(0, this.maxBatchSize);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Correlation-Id": this.correlationId,
    });

    this.api
      .postPath(this.endpoint, { events: batch }, { headers })
      .pipe(
        finalize(() => {
          if (this.rotateCorrelationAfterNextFlush) {
            this.rotateCorrelationAfterNextFlush = false;
            this.rotateCorrelationId();
          }
        })
      )
      .subscribe({ error: () => {} });
  }

  private hasValidAuthForClientEvents(): boolean {
    const token = this.auth.getToken();
    const user = this.auth.getCurrentUser();
    if (!token || !user) return false;
    return !this.auth.isTokenExpired();
  }

  private rotateCorrelationId(): void {
    this.correlationId = rotateAuditSessionCorrelationId();
    try {
      sessionStorage.removeItem(this.sessionEndSentKey);
    } catch {
      // ignore
    }
  }

  private sendSessionEndBeacon(): void {
    if (!this.hasValidAuthForClientEvents()) return;
    const token = this.auth.getToken();
    if (!token) return;
    // If we already emitted a terminal auth event (logout/token-expired) very recently,
    // don't also send "session end" — it's redundant noise in audit.
    if (this.authTerminalSentAtUtcMs && Date.now() - this.authTerminalSentAtUtcMs < 15_000) return;
    // Only send once per correlation/session (refresh/suspend can trigger pagehide multiple times).
    try {
      const already = sessionStorage.getItem(this.sessionEndSentKey);
      if (already && already === this.correlationId) return;
      sessionStorage.setItem(this.sessionEndSentKey, this.correlationId);
    } catch {
      // ignore (best effort)
    }

    const body = JSON.stringify({
      events: [
        {
          eventType: "AUTH_SESSION_END",
          route: this.router.url,
          label: "pagehide",
          correlationId: this.correlationId,
          createdAtUtc: centralDateTimeToPlainBackend(),
          metadataJson: JSON.stringify({ reason: "pagehide" }),
        },
      ],
    });

    try {
      void fetch(this.api.resolveUrl(this.endpoint), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Correlation-Id": this.correlationId,
        },
        body,
        keepalive: true,
      });
    } catch {
      // ignore
    }
  }
}

